import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LibraryProvider } from "./context/LibraryContext";

import BookSplash from "./components/BookSplash";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Stats from "./components/Stats";
import FeaturedBooks from "./components/FeaturedBooks";
import Audiobooks from "./components/Audiobooks";
import LibraryFeatures from "./components/LibraryFeatures";
import CelebrityEvents from "./components/CelebrityEvents";
import BlogsSection from "./components/BlogsSection";
import SubscriptionPlans from "./components/SubscriptionPlans";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import SEO, { AnalyticsTags } from "./components/SEO";
import { pageSeo, siteSeo, buildAbsoluteUrl } from "./seo/seoConfig";
import "./App.css";

const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const AuthModal = lazy(() => import("./components/auth/AuthModal"));
const SubscriptionGate = lazy(() => import("./components/auth/SubscriptionGate"));
const BookDetail = lazy(() => import("./components/BookDetail"));
const BookReader = lazy(() => import("./components/BookReader"));
const EventDetail = lazy(() => import("./components/EventDetail"));
const BlogDetail = lazy(() => import("./components/BlogDetail"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PaymentStatusPage = lazy(() => import("./pages/PaymentStatusPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const ShippingInfoPage = lazy(() => import("./pages/ShippingInfoPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const StaticPage = lazy(() => import("./components/StaticPage"));
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const BooksPage = lazy(() => import("./pages/BooksPage"));
const AudiobooksPage = lazy(() => import("./pages/AudiobooksPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const AboutAuthorPage = lazy(() => import("./pages/AboutAuthorPage"));

/**
 * AppOverlays
 * Renders auth overlays ON TOP of the existing app
 */
function AppOverlays() {
  const { showAuthModal, showSubGate } = useAuth();
  return (
    <>
      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal />
        </Suspense>
      )}
      {showSubGate && (
        <Suspense fallback={null}>
          <SubscriptionGate />
        </Suspense>
      )}
    </>
  );
}

function AppShell() {
  const location = useLocation();
  const isReader = location.pathname.startsWith("/reader/");

  if (isReader) return null;

  return (
    <>
      <BookSplash />
      <Navbar />
      <WhatsAppFloat />
    </>
  );
}

function HomeLayout() {
  const location = useLocation();

  // Handle scroll-to anchor navigation
  useEffect(() => {
    if (location.state && location.state.scrollTo) {
      const targetId = location.state.scrollTo;
      const doScroll = () => {
        const target = document.getElementById(targetId);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      
      doScroll();
      const timer = setTimeout(doScroll, 100);
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location]);

  return (
    <>
      <SEO
        {...pageSeo.home}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: siteSeo.siteName,
            url: siteSeo.siteUrl,
            logo: buildAbsoluteUrl("/logo.png"),
            founder: siteSeo.author,
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: siteSeo.siteName,
            url: siteSeo.siteUrl,
            publisher: {
              "@type": "Organization",
              name: siteSeo.siteName,
            },
          },
        ]}
      />
      <Hero />
      <Stats />
      <FeaturedBooks preview />
      <Audiobooks preview />
      <LibraryFeatures />
      <CelebrityEvents preview />
      <BlogsSection />
      <SubscriptionPlans />
      <Footer />
    </>
  );
}

function RevealObserver() {
  const location = useLocation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px 50px 0px" }
    );

    const observeElements = () => {
      const elements = document.querySelectorAll(".reveal:not(.in-view)");
      elements.forEach((el) => {
        observer.observe(el);
      });
    };

    observeElements();
    const t1 = setTimeout(observeElements, 100);
    const t2 = setTimeout(observeElements, 400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
    };
  }, [location.pathname]);

  return null;
}

function ScrollToTop() {
  const location = useLocation();

  useLayoutEffect(() => {
    if (location.pathname === "/" && location.state?.scrollTo) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.state]);

  return null;
}

function SeoRoute({ seo, children }) {
  return (
    <>
      <SEO {...seo} />
      {children}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <LibraryProvider>
        <BrowserRouter>
          <AnalyticsTags />
          <AppShell />
          <ScrollToTop />
          <RevealObserver />
          <main id="main-content">
            <Suspense fallback={null}>
              <Routes>
                <Route path="/"                                 element={<HomeLayout />} />
                <Route path="/library"                          element={<SeoRoute seo={pageSeo.library}><LibraryPage /></SeoRoute>} />
                <Route path="/book/:id"                         element={<BookDetail />} />
                <Route path="/books"                            element={<SeoRoute seo={pageSeo.books}><BooksPage /></SeoRoute>} />
                <Route path="/audiobooks"                       element={<SeoRoute seo={pageSeo.audiobooks}><AudiobooksPage /></SeoRoute>} />
                <Route path="/reader/:id"                       element={<SeoRoute seo={pageSeo.reader}><BookReader /></SeoRoute>} />
                <Route path="/event/:id"                        element={<EventDetail />} />
                <Route path="/events"                           element={<SeoRoute seo={pageSeo.events}><EventsPage /></SeoRoute>} />
                <Route path="/blogs"                            element={<SeoRoute seo={pageSeo.blogs}><BlogsPage /></SeoRoute>} />
                <Route path="/blog/:id"                         element={<BlogDetail />} />
                <Route path="/about-author"                     element={<SeoRoute seo={pageSeo.aboutAuthor}><AboutAuthorPage /></SeoRoute>} />
                <Route path="/payment/:type/:itemName/:price"   element={<SeoRoute seo={pageSeo.checkout}><CheckoutPage /></SeoRoute>} />
                <Route path="/payment-status/:paymentId"         element={<SeoRoute seo={pageSeo.paymentStatus}><PaymentStatusPage /></SeoRoute>} />
                <Route path="/profile"                          element={<SeoRoute seo={pageSeo.profile}><ProfilePage /></SeoRoute>} />

                {/* Footer Static Pages */}
                <Route path="/help"     element={<SeoRoute seo={pageSeo.help}><StaticPage title="Help Center" /></SeoRoute>} />
                <Route path="/faqs"     element={<SeoRoute seo={pageSeo.faqs}><StaticPage title="Frequently Asked Questions" /></SeoRoute>} />
                <Route path="/contact"  element={<SeoRoute seo={pageSeo.contact}><ContactPage /></SeoRoute>} />
                <Route path="/shipping" element={<SeoRoute seo={pageSeo.shipping}><ShippingInfoPage /></SeoRoute>} />
                <Route path="/returns"  element={<SeoRoute seo={pageSeo.refund}><StaticPage title="Return Policy" /></SeoRoute>} />
                <Route path="/privacy"  element={<SeoRoute seo={pageSeo.privacy}><PrivacyPolicyPage /></SeoRoute>} />
                <Route path="/refund"   element={<SeoRoute seo={pageSeo.refund}><RefundPolicyPage /></SeoRoute>} />
                <Route path="/terms"    element={<SeoRoute seo={pageSeo.terms}><TermsPage /></SeoRoute>} />
              </Routes>
            </Suspense>
          </main>

          {/* Auth overlays */}
          <AppOverlays />
        </BrowserRouter>
      </LibraryProvider>
    </AuthProvider>
  );
}

export default App;
